import React, { useCallback } from 'react';
import { isNull } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { SingleField } from '../fields/SingleField';
import { getOpenFieldsAndSections, toggleOpenFieldSection } from '../store';
import { EarlyTerminationAndCloseOutNetting, UpdatedOpinionField } from '../store/types';
import styles from './OpinionSection.module.scss';
import { SectionWrapper } from './SectionWrapper';

interface NettingEarlyTerminationSectionProps {
    earlyTermination: EarlyTerminationAndCloseOutNetting;
    previousEarlyTermination: EarlyTerminationAndCloseOutNetting | null;
    isEditing: boolean;
    isUpdating: boolean;
    fieldsUpdated: UpdatedOpinionField[];
    nettingEngineFields: string[];
}

export const NettingEarlyTerminationSection: React.FC<NettingEarlyTerminationSectionProps> = ({ earlyTermination, previousEarlyTermination, isEditing, isUpdating, fieldsUpdated, nettingEngineFields }) => {
    const dispatch = useAppDispatch();
    const openFieldsAndSections = useAppSelector(getOpenFieldsAndSections);
    const getFieldOpen = useCallback((id: string) => openFieldsAndSections.includes(id), [openFieldsAndSections]);
    const toggleFieldOpen = useCallback((id: string) => dispatch(toggleOpenFieldSection(id)), [dispatch]);
    const sectionId = 'earlyTermination';
    const { optionalEarlyTermination, closeOutNetting, closeOutNettingNotCovered, aetEnforceable, aetRequired, aetAdvisable, bridgesOne, bridgesTwo, closeOutProtocol, sectionTwoAThree, paymentNettingTwoC, nonInsolvency, insolvency, closeOutLocalCurrency } = earlyTermination;

    const getPreviousField = useCallback((fieldId: keyof EarlyTerminationAndCloseOutNetting) => !isNull(previousEarlyTermination) && previousEarlyTermination[fieldId] || null, [previousEarlyTermination]);

    return (
        <SectionWrapper id={sectionId} label='Early Termination and Close-Out Netting' isEditing={isEditing} isUpdating={isUpdating} nettingEngineFields={nettingEngineFields}>
            <div className={styles.earlyTerminationSectionWrapper}>
                <div className={styles.sectionHeaderLabel}>Under the laws of the jurisdiction under consideration:</div>
                <SingleField
                    field={optionalEarlyTermination}
                    previousField={getPreviousField('optionalEarlyTermination')}
                    fieldId='optionalEarlyTermination'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={closeOutNetting}
                    previousField={getPreviousField('closeOutNetting')}
                    fieldId='closeOutNetting'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    nettingEngineFields={nettingEngineFields}
                />
                <SingleField
                    field={closeOutNettingNotCovered}
                    previousField={getPreviousField('closeOutNettingNotCovered')}
                    fieldId='closeOutNettingNotCovered'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={aetEnforceable}
                    previousField={getPreviousField('aetEnforceable')}
                    fieldId='aetEnforceable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={aetRequired}
                    previousField={getPreviousField('aetRequired')}
                    fieldId='aetRequired'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={[]}
                    nettingEngineFields={nettingEngineFields}
                    overrideDisabled
                    guidanceNotes={['This field is derived from the information in the counterparty coverage section, but details can still be added here.']}
                />
                <SingleField
                    field={aetAdvisable}
                    previousField={getPreviousField('aetAdvisable')}
                    fieldId='aetAdvisable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={bridgesOne}
                    previousField={getPreviousField('bridgesOne')}
                    fieldId='bridgesOne'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={bridgesTwo}
                    previousField={getPreviousField('bridgesTwo')}
                    fieldId='bridgesTwo'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={closeOutProtocol}
                    previousField={getPreviousField('closeOutProtocol')}
                    fieldId='closeOutProtocol'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={sectionTwoAThree}
                    previousField={getPreviousField('sectionTwoAThree')}
                    fieldId='sectionTwoAThree'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={paymentNettingTwoC}
                    previousField={getPreviousField('paymentNettingTwoC')}
                    fieldId='paymentNettingTwoC'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={nonInsolvency}
                    previousField={getPreviousField('nonInsolvency')}
                    fieldId='nonInsolvency'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={insolvency}
                    previousField={getPreviousField('insolvency')}
                    fieldId='insolvency'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={closeOutLocalCurrency}
                    previousField={getPreviousField('closeOutLocalCurrency')}
                    fieldId='closeOutLocalCurrency'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
            </div>
        </SectionWrapper>
    );
};
